<script setup lang="ts">
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiErrorViewProps {
  error: any;
}
const props = withDefaults(defineProps<CiErrorViewProps>(), {});

/********************
 * COMPOSITIONS      *
 ********************/
const contentStore = useContentStore();
const localePath = useLocalePath();
const { $pgettext, $gettext } = useGettext();

useHead({
  title: getMetaTranslation('defaultTitle'),
  meta: [
    {
      name: 'description',
      content: getMetaTranslation('defaultDescription'),
    },
  ],
});

/********************
 * REFS & VARS       *
 ********************/
const statusCode = computed(() => {
  return props.error?.statusCode;
});

const is404 = computed(() => {
  return statusCode.value === 404;
});

const is410 = computed(() => {
  return statusCode.value === 410;
});

const subline = computed(() => {
  return is404.value || is410.value
    ? $gettext('Wrong link? Typo? No worries – the next campsite is just one click away.')
    : $gettext('Sorry, something went wrong. Please try again.');
});

const headline = computed(() => {
  switch (true) {
    case statusCode.value === 404:
      return $pgettext('error page 404 headline', 'Oops! Looks like you took a wrong turn.');

    case statusCode.value >= 500:
      return $pgettext('error page 5xx headline', 'Oh no, a server error occurred!');

    default:
      return contentStore.h1;
  }
});

/********************
 * FUNCTIONS         *
 ********************/
function getMetaTranslation(key) {
  const t = metaData.error[key];
  if (t !== undefined && typeof t === 'object') {
    return $pgettext(t.ctx, t.key);
  }
  return t;
}
</script>

<template>
  <div class="error-view container min-h-full py-12 text-center">
    <h1 class="mb-6 text-center">
      {{ headline }}
    </h1>
    <p>{{ subline }}</p>
    <p v-if="!is404 && !is410" class="text-gray-50 text-xs">
      Error code: {{ statusCode }}
    </p>

    

    <CiButton tag="NuxtLink" :href="localePath('index')" class="mt-2">
      {{ $gettext('Discover campsites') }}
      <CiAwesomeIcon class="ml-2" :icon="faChevronRight" ratio="0.6" />
    </CiButton>

    <img
      v-if="is404 || is410"
      class="size-full"
      loading="lazy"
      src="~/assets/img/404-mask-min.png"
      alt="404"
    />
  </div>
</template>
